import { Trans, useTranslation } from 'react-i18next';
import React, { memo, useState, useMemo, useCallback, useRef } from 'react';
import Skeleton from 'react-loading-skeleton';
import cx from 'clsx';
import { CommentEligibilities, CommentEligibilityModal } from '@debank/content-editor';
import {
  IconRcFeedReply,
  IconRcFeedPined,
  IconRcStreamCommentEditor,
  IconRcFeedLike,
  IconRcFeedLiked,
} from '@/assets/stream';
import {
  Button,
  UnionAvatar,
  HelperTooltip,
  CompatibleEntityContent,
  UserCardPopup,
  UnionFollowButtonV2,
  Tips,
  LazyMediaSized,
  VipLogoWithGift,
  Translated,
  TernaryTranslate,
} from '@/components';
import {
  useCurrentAddressInfo,
  useSwitch,
  useDebounceCallback,
  useDeviceTypeIsMobile,
  useImagePreview,
  useTheme,
} from '@/hooks';
import {
  login,
  isOfficalAddress,
  toastError,
  openInDebank,
  openNewWindow,
  getOfficialPath,
} from '@/utils';

import { FeedComment, FeedArticleDetail, FeedArticle } from '../../types';
import { useArticleComments, useCommentTrust, useArticleEditCommentEligibility } from '../../hooks';
import style from './index.module.scss';
import { CommentOrder } from '../CommentOrder';
import { useCommentOrder } from '../../state';
import { CommentMoreAction } from '../CommentMoreAction';
import { ArticleCreateCommentModal } from '../ArticleCreateCommentModal';
import { useEligibilityBadgeName } from '@/hooks/useEligibilityBadgeName';
import { useGroupDetail } from '@/views/Group/hooks';
import { SuicideTag } from '@/views/Profile/components/SuicideTag';
import { Virtuoso } from 'react-virtuoso';
import { FoldedComments } from '../FoldedComments';
import { isNumber } from 'lodash';

const OPERACTION_ALIGN = {
  targetOffset: [6, -4] as [number, number],
};

const SkeletonList = memo(({ count = 5 }) => (
  <>
    {Array(count)
      .fill(0)
      .map((_, i) => (
        <div className={cx(style.item, style.skeletonItem)} key={i}>
          <Skeleton circle height={32} width={32} className={style.avatar} />
          <div className={style.commentMain}>
            <Skeleton width={35} className={style.commentHead} />
            <Skeleton width={100} className={style.content} />
          </div>
        </div>
      ))}
  </>
));

const ISPAID_POST = ['pay_thread', 'pay_qa'];

export const ArticleComments = memo(
  ({
    className,
    containerClassName,
    feed,
    disabled,
    needPaid,
    articleId,
    onPermissionChanged,
  }: {
    className?: string;
    containerClassName?: string;
    feed?: FeedArticleDetail;
    disabled?: boolean;
    needPaid?: boolean;
    articleId: number;
    onPermissionChanged?(): void;
  }) => {
    const { t } = useTranslation();

    const { address, isVerified } = useCurrentAddressInfo();
    const isMobile = useDeviceTypeIsMobile();
    const { on, turnOn, turnOff } = useSwitch();
    const [replyTo, setReplyTo] = useState<FeedComment>();
    const [commentOrderBy, setCommentOrderBy] = useCommentOrder();
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const { isDark } = useTheme();

    const isSelf = useMemo(
      () => isVerified && address === feed?.article.creator_id,
      [feed?.article.creator_id, address, isVerified],
    );

    const isPayPost = useMemo(
      () => feed?.article.type && ISPAID_POST.includes(feed.article.type),
      [feed?.article.type],
    );

    const { mutateAsync: updateCommentEligibility } = useArticleEditCommentEligibility(articleId);

    const {
      on: commentEligiVisible,
      turnOn: turnOnCommentEligi,
      turnOff: turnOffCommentEligi,
    } = useSwitch();

    const { data, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } = useArticleComments({
      id: articleId,
      orderBy: commentOrderBy,
    });
    const { data: foldComments, isLoading: isLoadingFoldComments } = useArticleComments({
      id: articleId,
      orderBy: commentOrderBy,
      isLowQuality: true,
    });

    const badgeName = useEligibilityBadgeName({
      badgeEligibility: feed?.article?.permissions?.has_one_of_badges,
    });

    const { data: groupDetail } = useGroupDetail(feed?.article?.permissions?.is_group_member);
    const unFoldCommentCount = useMemo(
      () => data?.pages?.[data?.pages?.length - 1]?.total_count,
      [data?.pages],
    );
    const foldCommentCount = useMemo(
      () => foldComments?.pages?.[foldComments?.pages?.length - 1]?.total_count,
      [foldComments?.pages],
    );
    const totalCount = useMemo(
      () =>
        isNumber(unFoldCommentCount) && isNumber(foldCommentCount)
          ? unFoldCommentCount + foldCommentCount
          : null,
      [foldCommentCount, unFoldCommentCount],
    );
    const comments = useMemo(
      () => data?.pages?.flatMap(x => x.comments)?.filter(comment => !comment._is_hidden),
      [data],
    );

    // const showCommentFolder = useMemo(
    //   () => unFoldCommentCount && comments?.length && unFoldCommentCount >= comments.length,
    //   [comments?.length, unFoldCommentCount],
    // );

    const fetchMore = useCallback(() => {
      if (hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

    const handleReplyTo = useCallback(
      (c: FeedComment) => {
        if (disabled) {
          return;
        }

        if (!address) {
          login();
          return;
        }

        setReplyTo(c);
        turnOn();
      },
      [turnOn, disabled, address],
    );

    const handleCommentClick = useCallback(() => {
      if (disabled) {
        return;
      }
      if (isMobile) {
        openInDebank({ link: `/stream/${articleId}` });
        return;
      }

      setReplyTo(undefined);
      turnOn();
    }, [disabled, isMobile, turnOn, articleId]);

    const handleRestrictClick = useCallback(() => {
      turnOnCommentEligi();
    }, [turnOnCommentEligi]);

    const handleCommentEligi = useCallback(
      async data => {
        try {
          await updateCommentEligibility(data);
          onPermissionChanged?.();
        } catch (error) {
          toastError((error as string) || 'Failed');
        }
      },
      [updateCommentEligibility, onPermissionChanged],
    );

    const handleOrderByChange = useDebounceCallback(value => {
      setCommentOrderBy(value);
    }, 300);

    const commentRestrictions = useMemo(() => {
      return (
        feed?.article.permissions &&
        // eslint-disable-next-line array-callback-return
        CommentEligibilities.map(x => {
          if (x.name in feed.article.permissions) {
            if (x.name === 'has_one_of_badges') {
              // @ts-ignore
              return x.desc?.(badgeName);
            } else if (x.name === 'is_group_member') {
              // @ts-ignore
              return x.desc?.(groupDetail?.name);
            }
            // @ts-ignore
            return x.desc(feed.article.permissions[x.name]);
          }
        }).filter(x => !!x)
      );
    }, [badgeName, feed?.article.permissions, groupDetail?.name]);

    const commentTip = useMemo(() => {
      if (!feed?.article?.has_permission) {
        return t('ArticleContent.disabledComment');
      }
      if (needPaid) {
        return t('ArticleContent.paidTip');
      }
      return null;
    }, [feed?.article?.has_permission, needPaid, t]);

    const renderFooter = useCallback(() => {
      if (isFetchingNextPage) {
        return <SkeletonList />;
      }
      if (hasNextPage) {
        return null;
      }
      return (
        <FoldedComments
          article={feed?.article!}
          commentOrderBy={commentOrderBy}
          articleId={articleId}
          handleReplyTo={handleReplyTo}
        />
      );
    }, [articleId, commentOrderBy, feed?.article, handleReplyTo, hasNextPage, isFetchingNextPage]);

    return (
      <div className={cx(style.comments, containerClassName)} ref={scrollContainerRef}>
        <div className={style.header}>
          <div className={style.left}>
            <span className={style.title}>
              {t('ArticleComments.1q4xqtw')}
              {totalCount ? <span className={style.commentCount}> ({totalCount})</span> : null}
            </span>
            {!isMobile && <CommentOrder orderBy={commentOrderBy} onChange={handleOrderByChange} />}
          </div>
          <HelperTooltip title={commentTip}>
            <Button
              className={style.addBtn}
              type="primary"
              onClick={handleCommentClick}
              disabled={disabled || !address}>
              {t('ArticleComments.p1t9ds')}
            </Button>
          </HelperTooltip>
        </div>
        {needPaid ? (
          <div
            className={cx(
              style.hiddenComments,
              feed?.article.comment_count && style.hasComments,
              isDark && style.isDark,
            )}>
            {feed?.article.comment_count
              ? t('ArticleComments.commentsPaidTip', { amount: feed.article._commentCount })
              : t('ArticleComments.noComments')}
          </div>
        ) : (
          <>
            {isMobile && (
              <CommentOrder
                className={style.mobileOrderContainer}
                orderBy={commentOrderBy}
                onChange={handleOrderByChange}
              />
            )}
            <div className={style.commentEligi}>
              <span className={style.commentEligiTitle}>{t('ArticleComments.ba2424')}</span>
              {commentRestrictions?.length ? (
                <Trans i18nKey="ArticleComments.197p2h8" count={commentRestrictions?.length || 0}>
                  <span className={style.commentEligiCount}>{commentRestrictions.length}</span>
                  restriction
                </Trans>
              ) : (
                t('RestrictionTip.1n92619')
              )}
              {isSelf ? (
                isPayPost ? null : (
                  <HelperTooltip
                    disable={!commentRestrictions?.length}
                    title={
                      <div className={style.commentEligiTipContent}>
                        {commentRestrictions?.map((x, i) => <div key={i}>• {x}</div>)}
                      </div>
                    }>
                    <IconRcStreamCommentEditor
                      className={style.commentEditorIcon}
                      onClick={handleRestrictClick}
                    />
                  </HelperTooltip>
                )
              ) : commentRestrictions?.length ? (
                <Tips
                  className={style.commentEligiTip}
                  iconClassName={style.commentEligiTipIcon}
                  title={
                    <div className={style.commentEligiTipContent}>
                      {commentRestrictions.map((x, i) => (
                        <div key={i}>• {x}</div>
                      ))}
                    </div>
                  }
                />
              ) : null}
            </div>
            {isLoading || isLoadingFoldComments ? (
              <SkeletonList />
            ) : comments?.length ? (
              <>
                <div className={className}>
                  <Virtuoso
                    useWindowScroll
                    data={comments}
                    overscan={10}
                    customScrollParent={document.querySelector('#detailModal') as HTMLElement}
                    endReached={fetchMore}
                    itemContent={(index, item) => (
                      <ArticleCommentItem
                        key={item.id}
                        comment={item}
                        articleId={articleId}
                        onReplyClick={handleReplyTo}
                        noBorder={!index}
                        disabled={disabled}
                        article={feed?.article}
                        articleInvisible={feed && !feed.article.is_visible}
                        isLowQuality={false}
                      />
                    )}
                    components={{
                      Footer: renderFooter,
                    }}
                  />
                </div>
              </>
            ) : foldCommentCount ? (
              <FoldedComments
                article={feed?.article!}
                commentOrderBy={commentOrderBy}
                articleId={articleId}
                handleReplyTo={handleReplyTo}
              />
            ) : (
              <div className={style.noComments}>{t('ArticleComments.meta57')}</div>
            )}
          </>
        )}
        <ArticleCreateCommentModal
          on={on}
          articleId={articleId}
          turnOff={turnOff}
          replyTo={replyTo}
        />
        {commentEligiVisible ? (
          <CommentEligibilityModal
            on={commentEligiVisible}
            turnOff={turnOffCommentEligi}
            onSubmit={handleCommentEligi}
            initEligibity={feed?.article.permissions}
          />
        ) : null}
      </div>
    );
  },
);

export const ArticleCommentItem = ({
  className,
  articleId,
  comment,
  onReplyClick,
  noBorder,
  disabled,
  articleInvisible,
  article,
  isLowQuality,
}: {
  className?: string;
  articleId: number;
  comment: FeedComment;
  onReplyClick?: (to: FeedComment) => void;
  noBorder?: boolean;
  disabled?: boolean;
  articleInvisible?: boolean;
  article?: FeedArticle;
  isLowQuality: boolean;
}) => {
  const { t } = useTranslation();

  const isMobile = useDeviceTypeIsMobile();
  const { isVerified } = useCurrentAddressInfo();
  const { mutate: trustArticle, isLoading: isTrusting } = useCommentTrust(
    comment.id,
    comment.is_trust,
  );
  const { handlePreview } = useImagePreview();

  const handleReplyClick = useCallback(() => {
    if (isMobile) {
      openInDebank({ link: `/stream/${articleId}` });
      return;
    }
    onReplyClick?.(comment);
  }, [articleId, comment, isMobile, onReplyClick]);

  const handleUserClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();

      const userId = comment.creator.id;
      const isOffical = isOfficalAddress(userId);

      openNewWindow(
        isOffical
          ? `${getOfficialPath(comment?.creator as OfficialAccount)}/stream`
          : `/profile/${userId}/stream`,
        '_blank',
      );
    },
    [comment.creator],
  );
  const [isTrust] = useMemo(
    () => [comment.is_trust && isVerified, comment.is_trust === false && isVerified],
    [comment.is_trust, isVerified],
  );

  const isSuicide = useMemo(
    () => (comment?.creator as GeneralAccount)?.desc?.user?.is_suicide,
    [comment?.creator],
  );

  // const handleDistrustClick = useCallback(
  //   (e: React.MouseEvent<HTMLDivElement>) => {
  //     e.stopPropagation();
  //     if (isTrusting) {
  //       return;
  //     }
  //     if (isMobile) {
  //       openInDebank({ link: `/stream/${articleId}` });
  //       return;
  //     }

  //     if (!isVerified) {
  //       login();
  //       return;
  //     }

  //     trustArticle(isDistrust ? null : false);
  //   },
  //   [isTrusting, isMobile, isVerified, trustArticle, isDistrust, articleId],
  // );
  const handleImageClick = useCallback(
    (e: React.MouseEvent<HTMLImageElement>) => {
      handlePreview(e);
      e.stopPropagation();
    },
    [handlePreview],
  );

  const handleTrustClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      if (isTrusting) {
        return;
      }
      if (isMobile) {
        openInDebank({ link: `/stream/${articleId}` });
        return;
      }
      if (!isVerified) {
        login();
        return;
      }

      trustArticle(isTrust ? null : true);
    },
    [articleId, isMobile, isTrust, isTrusting, isVerified, trustArticle],
  );

  return (
    <>
      <div
        className={cx(
          style.item,
          noBorder && style.isNoBorder,
          isMobile && style.isMobile,
          className,
        )}>
        <div className={style.status}>
          {comment?.is_pin && (
            <div className={style.pinned}>
              <IconRcFeedPined />
              <span className={style.pinnedText}>{t('ArticleComments.979ywa')}</span>
            </div>
          )}
        </div>
        <div className={style.commentContainer}>
          <div onClick={handleUserClick}>
            <UnionAvatar
              className={style.avatar}
              size={isMobile ? 38 : 48}
              data={comment.creator}
              userCardPopup
            />
          </div>
          <div className={style.commentMain}>
            <div className={style.commentHead}>
              <div className={style.creator}>
                <UserCardPopup address={String(comment.creator.id) || ''}>
                  <div onClick={handleUserClick} className={style.uid}>
                    {isSuicide ? <SuicideTag userAddress={comment.creator.id} /> : comment._author}
                  </div>
                </UserCardPopup>
                {comment._showVip ? (
                  <VipLogoWithGift
                    userInfo={comment.creator as UserInfo}
                    marginLeft={4}
                    marginRight={4}
                  />
                ) : null}
                <HelperTooltip title={comment._fulltime}>
                  <div className={style.createdAt}>{`· ${comment._daytime}`}</div>
                </HelperTooltip>
                {comment.creator_id ? (
                  <UnionFollowButtonV2
                    address={comment.creator_id}
                    isFollowing={comment.creator?.is_following}
                  />
                ) : null}
              </div>
              <CommentMoreAction
                comment={comment}
                articleId={articleId}
                article={article}
                isLowQuality={isLowQuality}
              />
            </div>
            <div className={cx(style.content, articleInvisible && style.invisible)}>
              {articleInvisible ? (
                <div className={style.invisibleComment} />
              ) : (
                <>
                  {comment.reply_to_user ? (
                    comment.reply_to_comment_content ? (
                      <div className={style.replyContentWrap}>
                        <Translated
                          creatorId={`${comment.reply_to_user.id}`.toLocaleLowerCase()}
                          canTranslated={!!comment.reply_to_comment_content_translation}
                          btnClassName={style.replyTransBtn}
                          contentType="comment">
                          <div className={cx(style.replyContent, isMobile && style.isMobile)}>
                            <UserCardPopup
                              className={style.replyUser}
                              address={String(comment.reply_to_user!.id) || ''}>
                              <span onClick={handleUserClick}>{comment._replyTo}</span>:
                            </UserCardPopup>
                            <TernaryTranslate
                              original={comment.reply_to_comment_content}
                              translated={comment.reply_to_comment_content_translation}
                            />
                          </div>
                        </Translated>
                      </div>
                    ) : (
                      <span className={style.contentReply}>
                        {t('ArticleComments.mx9569')}
                        {comment._replyTo}:{' '}
                      </span>
                    )
                  ) : null}
                  <CompatibleEntityContent
                    content={comment.content}
                    entities={comment.entities}
                    content_translation={comment.content_translation}
                    entities_translation={comment.entities_translation}
                    creator_id={comment.creator_id}
                    contentType="comment"
                  />
                  {comment.images?.map((x, i) => (
                    <LazyMediaSized
                      className={style.commentImage}
                      src={x.raw.full_url}
                      onClick={handleImageClick}
                      key={i}
                      maxHeight={100}
                      maxWidth={isMobile ? window.screen.width - 102 : 200}
                      height={x.raw.height}
                      width={x.raw.width}
                    />
                  ))}
                </>
              )}
            </div>
            <div className={style.operationContainer}>
              <div className={style.commentOperation}>
                <HelperTooltip
                  disable={isMobile}
                  title={isTrust ? t('ArticleContent.cancelTrust') : t('ArticleContent.trust')}
                  placement="topLeft"
                  align={OPERACTION_ALIGN}>
                  <div
                    className={cx(style.operation, isTrust && style.isTrust)}
                    onClick={handleTrustClick}>
                    <div className={cx(style.opIconWrap)}>
                      {isTrust ? <IconRcFeedLiked /> : <IconRcFeedLike />}
                    </div>
                    {comment._trustCount}
                  </div>
                </HelperTooltip>
                {/* <HelperTooltip
                  disable={isMobile}
                  title={isDistrust ? 'Cancel Distrust' : 'Distrust'}
                  placement="topLeft"
                  align={OPERACTION_ALIGN}>
                  <div
                    className={cx(style.operation, isDistrust && style.isDistrust)}
                    onClick={handleDistrustClick}>
                    <div className={cx(style.opIconWrap)}>
                      <IconRcFeedDistrust color={isDistrust ? 'var(--color-red)' : ''} />
                    </div>
                    {comment._distrustCount}
                  </div>
                </HelperTooltip> */}
                <HelperTooltip
                  disable={isMobile}
                  title={
                    disabled ? t('ArticleContent.disabledComment') : t('ArticleComments.comment')
                  }
                  placement="topLeft"
                  align={OPERACTION_ALIGN}>
                  <div
                    className={cx(style.operation, style.replyIcon, disabled && style.isDisable)}
                    onClick={handleReplyClick}>
                    <div className={cx(style.opIconWrap, style.replyIconWrap)}>
                      <IconRcFeedReply />
                    </div>
                  </div>
                </HelperTooltip>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
